import { Expose, Exclude, Transform } from 'class-transformer'
import { DocumentSnapshot } from 'firebase/firestore'
import { _FirebaseDocument, anyToDate } from './Models'

/**
 * A position on a line of a v3 report (MYAC-1496 lot 1, step 6), at
 * `projects/{pid}/regulation_reports/{rid}/positions/{row_id}`. Written by the
 * server only, when it accepts a `position_set` command; the page reads it.
 * An unknown decision degrades to `'unknown'`.
 */
export const POSITION_DECISIONS = ['follow_new_version', 'keep_reference', 'not_applicable'] as const
export type PositionDecisionV3 = (typeof POSITION_DECISIONS)[number] | 'unknown'

export function toPositionDecision(v: unknown): PositionDecisionV3 {
  return (POSITION_DECISIONS as readonly string[]).includes(v as string) ? (v as PositionDecisionV3) : 'unknown'
}

/** What the page shows for a line: given in this report, or carried from the
 *  last finalised one (step 7). */
export interface LinePositionV3 {
  row_id: string
  decision: PositionDecisionV3
  comment: string
  decided_by: string | null
  decided_at: Date | null
  /** Set when the position was carried from an earlier report. */
  from_report_id: string | null
}

function str(v: unknown): string {
  return typeof v === 'string' ? v : ''
}

export function parseCarriedPosition(raw: unknown): LinePositionV3 | null {
  if (!raw || typeof raw !== 'object') return null
  const r = raw as Record<string, unknown>
  if (!str(r.row_id)) return null
  return {
    row_id: str(r.row_id),
    decision: toPositionDecision(r.decision),
    comment: str(r.comment),
    decided_by: str(r.decided_by) || null,
    decided_at: anyToDate({ obj: r, key: 'decided_at' }) ?? null,
    from_report_id: str(r.from_report_id) || null,
  }
}

@Exclude()
export class ReportPositionV3 extends _FirebaseDocument {
  @Expose()
  public row_id: string = ''

  @Expose()
  @Transform(({ obj, key }) => str(obj[key]))
  public card_id: string = ''

  @Expose()
  @Transform(({ obj, key }) => toPositionDecision(obj[key]))
  public decision: PositionDecisionV3 = 'unknown'

  @Expose()
  @Transform(({ obj, key }) => str(obj[key]))
  public comment: string = ''

  @Expose()
  @Transform(({ obj, key }) => str(obj[key]) || null)
  public decided_by: string | null = null

  @Expose()
  @Transform(anyToDate)
  public decided_at: Date | null = null

  asLine(): LinePositionV3 {
    return {
      row_id: this.row_id || this.id,
      decision: this.decision,
      comment: this.comment,
      decided_by: this.decided_by,
      decided_at: this.decided_at,
      from_report_id: null,
    }
  }

  collection(): string {
    return 'positions'
  }

  static fromFirebase(o: DocumentSnapshot): ReportPositionV3 | null {
    return _FirebaseDocument._fromFirebase(ReportPositionV3, o)
  }
}
